import { formatCopy } from "../../theme/copy";
import type { QuestionModule } from "../contracts";
import {
  textListCore,
  type TextListDefinition,
  type TextListDraft,
  type TextListEvent,
  type TextListRaw,
  type TextListResponse,
} from "./core";
import { SequentialTextListView, SimultaneousTextListView } from "./views";

export const textListModule: QuestionModule<
  TextListRaw,
  TextListDefinition,
  TextListDraft,
  TextListEvent,
  TextListResponse
> = {
  type: "text_list",
  galleryFeatures: [
    "baseline",
    "restored",
    "sequential",
    "simultaneous",
    "dont-know",
  ],
  core: textListCore,
  prompt: (definition) => ({
    content: definition.prompt,
    image: definition.image,
    altText: definition.altText,
    error: definition.error,
  }),
  views: {
    stepwise: {
      id: "stepwise",
      label: "Stepwise",
      component: SequentialTextListView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action", "text-input"],
      input: "host-text",
      responseFlow: "stepwise",
    },
    simultaneous: {
      id: "simultaneous",
      label: "All at once",
      component: SimultaneousTextListView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
      responseFlow: "all-at-once",
    },
  },
  defaultDisplay: { view: "stepwise" },
  controls: (definition, draft, display, copy) => {
    const isSequential = display.view === "stepwise";
    const lastRow = draft.activeIndex === definition.rows.length - 1;
    return {
      primaryAction: {
        purpose: isSequential && !lastRow ? "advance" : "submit",
        label: isSequential && !lastRow ? copy.actions.next : copy.actions.send,
        disabled:
          isSequential &&
          !lastRow &&
          definition.minAnswers === definition.rows.length &&
          !draft.values[draft.activeIndex].trim(),
        action:
          isSequential && !lastRow
            ? { type: "update", event: { type: "advance" } as TextListEvent }
            : { type: "submit" },
      },
      ...(definition.dontKnow
        ? {
            secondaryAction: {
              label: definition.dontKnow.label,
              selected: draft.dontKnowSelected,
              action: {
                type: "submit" as const,
                event: { type: "toggle-dont-know" as const },
              },
            },
          }
        : {}),
      ...(isSequential
        ? {
            textInput: {
              fieldId: `text-list-row-${draft.activeIndex + 1}`,
              label: formatCopy(copy.labels.response, {
                label: definition.rows[draft.activeIndex].label,
              }),
              value: draft.values[draft.activeIndex],
              suffix: `#${draft.activeIndex + 1}/${definition.rows.length}`,
              multiline: true,
              event: (value: string): TextListEvent => ({
                type: "set-row",
                index: draft.activeIndex,
                value,
              }),
            },
          }
        : {}),
    };
  },
};
